import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase.js";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";
import { parseISO, getISOWeek, format as fnsFormat } from "date-fns";
import Lightbox from "../components/common/Lightbox.js";

export default function GalleryPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [files, setFiles] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const day = searchParams.get("day");
  const index = searchParams.get("index");
  const client = searchParams.get("client");
  const uid = searchParams.get("uid");
  const slug = searchParams.get("slug");
  const weekStartParam = searchParams.get("weekStart");
  const jobId = searchParams.get("jobId");
  const dateParam = searchParams.get("date");

  // Convert Cloudinary URL to JPG format
  const getImageUrl = (url) => {
    if (!url) return url;
    return url.replace('/upload/', '/upload/f_jpg,q_auto/');
  };

  useEffect(() => {
    const loadFiles = async () => {
      // Check if it's a calendar job (has jobId) or weekly report (has day/index/uid/slug/weekStart)
      const isCalendarJob = !!jobId;

      if (!user) {
        setLoading(false);
        setError("Parâmetros inválidos na URL.");
        return;
      }

      if (isCalendarJob) {
        // Load from jobs collection
        if (!jobId || !dateParam) {
          setLoading(false);
          setError("Parâmetros inválidos na URL.");
          return;
        }

        try {
          console.log("🔍 Buscando galeria do job:", { jobId });

          const docRef = doc(db, "jobs", jobId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const data = docSnap.data();
            console.log("📄 Job encontrado!");
            console.log("📦 Arquivos:", data.uploadedFiles);

            if (data.uploadedFiles && data.uploadedFiles.length > 0) {
              setFiles(data.uploadedFiles);
            } else {
              console.log("❌ Nenhum arquivo no job");
              setFiles([]);
            }
          } else {
            console.log("❌ Job não existe:", jobId);
            setFiles([]);
          }
        } catch (err) {
          console.error("Erro ao carregar galeria do job:", err);
          setError("Erro ao carregar arquivos. Tente novamente.");
        } finally {
          setLoading(false);
        }
      } else {
        // Load from weeklyReports collection (original logic)
        if (!day || index === null || !uid || !slug || !weekStartParam) {
          setLoading(false);
          setError("Parâmetros inválidos na URL.");
          return;
        }

        try {
          // Usar a data da semana passada na URL
          const weekStartDate = parseISO(weekStartParam);
          const isoWeek = getISOWeek(weekStartDate);
          const reportId = `${uid}_${slug}_${fnsFormat(weekStartDate, "yyyy")}-W${isoWeek}`;

          console.log("🔍 Buscando galeria:", { reportId, day, index });

          // Buscar o documento do relatório semanal
          const docRef = doc(db, "weeklyReports", reportId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const data = docSnap.data();
            const entries = data.days?.[day] || [];

            console.log("📄 Documento encontrado!");
            console.log("📅 Entradas do dia:", entries);
            console.log("🔢 Index solicitado:", index);
            console.log("📦 Entrada específica:", entries[index]);

            if (entries[index] && entries[index].uploadedFiles) {
              const foundFiles = entries[index].uploadedFiles;
              console.log("✅ Arquivos encontrados:", foundFiles);
              console.log("🔍 Primeiro arquivo:", foundFiles[0]);
              console.log("🔍 Tipo do primeiro:", foundFiles[0]?.type);
              console.log("🔗 URL do primeiro:", foundFiles[0]?.url);
              setFiles(foundFiles);
            } else {
              console.log("❌ Nenhum arquivo na entrada");
              setFiles([]);
            }
          } else {
            console.log("❌ Documento não existe:", reportId);
            setFiles([]);
          }
        } catch (err) {
          console.error("Erro ao carregar galeria:", err);
          setError("Erro ao carregar arquivos. Tente novamente.");
        } finally {
          setLoading(false);
        }
      }
    };

    loadFiles();
  }, [user, day, index, uid, slug, weekStartParam, jobId, dateParam]);

  if (!user) {
    return (
      <Container className="py-5 text-center">
        <h5>{t(language, "accessDenied")}</h5>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h3>{t(language, "gallery")}</h3>
          <small className="text-muted">
            {dateParam || day} • {client}
          </small>
        </div>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          ← {t(language, "back")}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t(language, "loading")}</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">
          {error}
        </div>
      ) : files.length === 0 ? (
        <div className="alert alert-info">
          {t(language, "noFilesFound")}
        </div>
      ) : (
        <>
          <p className="text-muted mb-3">
            {language === "en"
              ? "Click on a photo to view fullscreen with navigation"
              : "Clique em uma foto para ver em tela cheia com navegação"}
          </p>
          <Row className="g-3">
            {files.map((file, idx) => {
              const imageUrl = getImageUrl(file.url);

              return (
                <Col key={idx} md={3} sm={6} xs={12}>
                  <div
                    className="border rounded overflow-hidden"
                    onClick={() => setLightboxIndex(idx)}
                    style={{ cursor: "pointer" }}
                  >
                    {file.type === "image" ? (
                      <img
                        src={imageUrl}
                        alt={file.name}
                        className="w-100"
                        style={{ height: "150px", objectFit: "cover" }}
                        onError={(e) => {
                          console.error("❌ Erro ao carregar imagem:", imageUrl);
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = `<div class="bg-danger text-white d-flex align-items-center justify-content-center" style="height: 150px"><small>Erro ao carregar</small></div>`;
                        }}
                        onLoad={() => console.log("✅ Imagem carregada:", file.name)}
                      />
                  ) : (
                    <div
                      className="bg-dark text-white d-flex align-items-center justify-content-center"
                      style={{ height: "150px" }}
                    >
                      <div className="text-center">
                        <div style={{ fontSize: "2rem" }}>🎥</div>
                        <small>Vídeo</small>
                      </div>
                    </div>
                  )}
                  <div className="p-2 bg-light">
                    <small className="d-block text-truncate text-dark">
                      {file.name}
                    </small>
                  </div>
                </div>
              </Col>
              );
            })}
          </Row>
        </>
      )}

      {/* Lightbox for fullscreen viewing with navigation */}
      {lightboxIndex !== null && files.length > 0 && (
        <Lightbox
          files={files}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(newIndex) => setLightboxIndex(newIndex)}
          getImageUrl={getImageUrl}
        />
      )}
    </Container>
  );
}
